/* eslint-disable react/forbid-prop-types */
import {
  InputAdornment,
  Alert,
  Box,
  Link as MuiLink,
  Paper,
  Grid,
  Typography,
  Button as MuiButton,
} from "@mui/material";
import AnnouncementIcon from "@mui/icons-material/Announcement";
import Email from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import { AuthAction, withUser as withAuthUser } from "next-firebase-auth";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { FormEventHandler, useEffect } from "react";
import AuthPageFrame from "../../components/auth/AuthPageFrame";
import CustomInput from "../../components/CustomInput/CustomInput";
import InfoArea from "../../components/InfoArea/InfoArea";
import Layout from "../../components/layout";
import { setFirebaseLanguage, signIn } from "../../services/firebase";
import { PageProps } from "../../types/page";
import { getBuildVersion } from "../../utilities/common";
import logger from "../../utilities/logger";

export async function getStaticProps(ctx: any) {
  const { locale } = ctx;
  const ssrConfig = await serverSideTranslations(locale, ["common", "login"]);
  const buildVersion = getBuildVersion();
  return {
    props: {
      ...ssrConfig,
      buildVersion,
    },
  };
}

function LoginPage({ buildVersion }: PageProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation("common");
  useEffect(() => {
    setFirebaseLanguage(language);
  }, [language]);
  const title = `${t("login:sectionTitle.login")}`;
  
  // State for form fields
  const [username, setUsername] = React.useState<string>("");
  const [password, setPassword] = React.useState<string>("");
  
  const [errorMsg, setErrorMsg] = React.useState<string>("");
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const router = useRouter();

  const onSubmit: FormEventHandler<HTMLFormElement> = React.useCallback(
    (event) => {
      event.preventDefault();
      setIsLoading(true);

      signIn(username, password)
        .then(() => {
          setIsLoading(false);
          router.push(`/`);
        })
        .catch((err) => {
          logger.error(err);
          setErrorMsg(t(`login:message.${err.code}`));
          setIsLoading(false);
        });
    },
    [username, password, language, t, router]
  );

  return (
    <Layout
      buildVersion={buildVersion}
      title={`${title} | Playwhat`}
      isHomepage
    >
        <AuthPageFrame maxWidth="md">
            <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
               <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 'bold' }}>
                  {title}
               </Typography>
               
               <Grid container spacing={4}>
                  <Grid item xs={12} md={5}>
                      <InfoArea
                        title={t("login:fieldLabel.forgetPassword")}
                        description={
                          <MuiLink component={Link} href="/users/forget-password" variant="body2" color="primary" fontWeight={500}>
                              {t("login:message.forgetPassword")}
                          </MuiLink>
                        }
                        icon={LockIcon}
                        iconColor="primary"
                      />
                      <InfoArea
                        title={t("login:fieldLabel.forgetEmail")}
                        description={t("login:message.systemUpgradeMessage")}
                        icon={AnnouncementIcon}
                        iconColor="info"
                      />
                  </Grid>
                  
                  <Grid item xs={12} md={7}>
                     {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}
                     
                     <form onSubmit={onSubmit}>
                        <CustomInput
                          id="email"
                          formControlProps={{
                            fullWidth: true,
                          }}
                          inputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Email />
                              </InputAdornment>
                            ),
                            placeholder: t("login:fieldLabel.username"),
                            value: username,
                            onChange: (e: React.ChangeEvent<HTMLInputElement>) => setUsername(e.target.value),
                            required: true,
                            type: "email"
                          }}
                        />
                        <CustomInput
                          id="password"
                          formControlProps={{
                            fullWidth: true,
                          }}
                          inputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <LockIcon />
                              </InputAdornment>
                            ),
                            placeholder: t("login:fieldLabel.password"),
                            type: "password",
                            value: password,
                            onChange: (e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value),
                            required: true,
                          }}
                        />
                        
                        <Box sx={{ textAlign: 'center', mt: 3 }}>
                           <MuiButton
                             variant="contained"
                             color="primary"
                             disabled={isLoading}
                             type="submit"
                             size="large"
                             fullWidth
                           >
                             {t("login:fieldLabel.login")}
                           </MuiButton>
                        </Box>
                        
                        <Box sx={{ textAlign: 'center', mt: 2 }}>
                           <MuiLink component={Link} href="/users/register" variant="body2" color="primary">
                              {t("login:fieldLabel.noAccountRegister", "Don't have an account? Register")}
                           </MuiLink>
                        </Box>
                     </form>
                  </Grid>
               </Grid>
            </Paper>
        </AuthPageFrame>
    </Layout>
  );
}

export default withAuthUser<PageProps>({
  whenAuthed: AuthAction.REDIRECT_TO_APP,
  whenAuthedBeforeRedirect: AuthAction.RETURN_NULL,
  whenUnauthedBeforeInit: AuthAction.SHOW_LOADER,
  whenUnauthedAfterInit: AuthAction.RENDER,
})(LoginPage);
